import React from 'react';
import Image from 'next/legacy/image';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';

import { ISplitFormFields } from '@/types/contentful';

import RichText from '../../RichText';
import { EmbeddedInlineEntryNode } from '../../RichText/Nodes';

import GlLink from '../../Link';
import { getImageData } from '../../../utils/contentful';

import tealBadge from '@/images/teal-badge.svg';
import checkmark from '@/images/check.svg';
import GlImage from '~/components/Image';
import { SocialShare } from '~/components/LearningCenter/Components/ArticleHeader/SocialShare';

interface SuccessProps
  extends Pick<
    ISplitFormFields,
    'successHeading' | 'successMessage' | 'successImage' | 'successSocialShare'
  > {
  customSuccessMessage?: string;
}

export function FormSuccess({
  successHeading,
  successMessage,
  successImage,
  customSuccessMessage,
  successSocialShare
}: SuccessProps) {
  return (
    <div className="flex w-full flex-col items-center justify-center px-6 py-16 desktop:p-16">
      <div className="relative mb-4 flex h-[88px] w-[88px] items-center justify-center">
        {successImage != null ? (
          <GlImage
            {...getImageData(successImage)}
            heroImage={false}
            layout="fill"
            objectFit="fill"
            alt="Success custom image for submitting form"
          />
        ) : (
          <>
            <div className="absolute z-[1] animate-rotation">
              <Image
                src={tealBadge}
                alt="Rotating green badge"
                height={88}
                width={88}
              />
            </div>
            <div className="absolute z-[2] mt-[10px]">
              <Image
                src={checkmark}
                alt="Success checkmark for submitting form"
                height={30}
                width={36}
              />
            </div>
          </>
        )}
      </div>

      <h4 className="mb-2 text-center font-medium desktop:text-[22px] desktop:leading-6">
        {successHeading}
      </h4>

      {successMessage && !customSuccessMessage && (
        <RichText
          data={successMessage}
          overrideOptions={{
            [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
              <p className="text-center font-normal text-black desktop:text-lg desktop:leading-6">
                {children}
              </p>
            ),
            [INLINES.HYPERLINK]: (node, children) => (
              <GlLink
                href={node.data.uri}
                className="text-black underline transition-colors duration-200 ease-in-out hover:text-navy desktop:hover:text-navy"
              >
                {children}
              </GlLink>
            ),
            [INLINES.EMBEDDED_ENTRY]: (node, children) => (
              <EmbeddedInlineEntryNode
                node={node}
                colour="defaultText"
                fontWeight="p"
              >
                {children}
              </EmbeddedInlineEntryNode>
            )
          }}
        />
      )}

      {customSuccessMessage && (
        <p className="text-center font-normal text-black desktop:text-lg desktop:leading-6">
          {customSuccessMessage}
        </p>
      )}
      <div className="mt-[10px] w-full items-center justify-center [&>div]:!justify-center">
        <SocialShare inline hideShareCopy socialOptions={successSocialShare} />
      </div>
    </div>
  );
}
